import { FC, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLoaderData } from "react-router";
import Title from "../components/ui/Title";
import Box from "../components/ui/Box";
import { AlertCircle } from "lucide-react";
import { Alert } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResetPassword } from "../lib/hooks/useResetPassword";

type FormValues = {
  password: string;
  confirmPassword: string;
};

interface LoadedData {
  token: string;
  isTokenValid: boolean;
}

const schema = z.object({
  password: z
    .string()
    .trim()
    .min(8, "8 caractères minimum")
    .max(32, "32 caractères maximum")
    .regex(
      RegExp("^(?=(.*[a-z]){1,})(?=(.*[A-Z]){1,})(?=(.*[0-9]){1,}).{8,}$"),
      "Votre mot de passe doit contenir au minimum une lettre en majuscule, une lettre en minuscule et un chiffre."
    ),
  confirmPassword: z.string().trim(),
});
export const ResetPasswordPage: FC = () => {
  const { isTokenValid, token } = useLoaderData() as LoadedData;
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { mutateAsync: resetPassword } = useResetPassword();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setError("");
    const { password, confirmPassword } = data;
    if (password.trim() !== confirmPassword.trim()) {
      form.setError("confirmPassword", {
        message: "Vos mots de passe ne correspondent pas.",
      });
      form.setError("password", {
        message: "Vos mots de passe ne correspondent pas.",
      });
      return;
    }
    try {
      await resetPassword({
        tokenId: token,
        password,
      });
      setSuccess(true);
    } catch (error: any) {
      console.error(error);
      setError(error?.response?.data?.message || "Une erreur s'est produite. Veuillez réessayer ultérieurement.");
    }
  };

  return (
    <div className='flex justify-center p-4 md:p-7'>
      <div className='mt-8'>
        <div>
          <Title className='text-center mb-5' order={1}>
            Nouveau mot de passe
          </Title>
          {isTokenValid && <div className='mb-4 text-center'>Veuillez saisir votre nouveau mot de passe.</div>}
        </div>
        <Box className='w-full md:w-[420px]'>
          {!isTokenValid && (
            <div className='flex gap-5'>
              <AlertCircle />
              <div>
                Ce lien de réinitialisation de mot de passe a expiré ou est invalide. Veuillez faire une nouvelle
                demande.
                <br />
                Vous pouvez fermer cet onglet.
              </div>
            </div>
          )}
          {isTokenValid && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                {!!error && <Alert variant={"destructive"}>{error}</Alert>}
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nouveau mot de passe</FormLabel>
                      <FormControl>
                        <Input type='password' required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='confirmPassword'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmer le mot de passe</FormLabel>
                      <FormControl>
                        <Input type='password' required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='flex flex-col gap-3 mt-2'>
                  {!success && (
                    <Button type='submit' loading={form.formState.isSubmitting}>
                      Valider
                    </Button>
                  )}
                  {success && (
                    <Alert className='bg-green-50 text-green-700'>
                      Mot de passe mis à jour avec succès. Vous pouvez fermer cet onglet.
                    </Alert>
                  )}
                </div>
              </form>
            </Form>
          )}
        </Box>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
